import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Lightbulb, BookOpen, ArrowRight, ArrowLeft, Timer, CheckCircle2, XCircle } from "lucide-react";
import { getProfile, QUESTIONS } from "@/data/practiceData";
import UserAvatar from "@/components/UserAvatar";
import PageBlobs from "@/components/PageBlobs";
import { UnderstandChapter } from "@/components/VideoLearning";

const TYPE_LABELS = {
  TABLE_FILL: "Table",
  TABLE_READ: "Table",
  STATEMENT_NUMERICAL: "Numerical",
  STATEMENT_THEORY: "Theory",
  MCQ: "MCQ",
  MATCH_COLUMNS: "Match",
  DIAGRAM_BASED: "Diagram",
};

const num = (v) => {
  const n = parseFloat(String(v).replace(/[₹,%\s]/g, "").replace(/:1$/, ""));
  return Number.isFinite(n) ? n : null;
};

const valuesMatch = (given, correct) => {
  const g = num(given);
  const c = num(correct);
  if (g !== null && c !== null) {
    if (c === 0) return g === 0;
    return Math.abs(g - c) / Math.abs(c) < 0.01;
  }
  return String(given).trim().toLowerCase() === String(correct).trim().toLowerCase();
};

const inputCls = (state) =>
  `w-full rounded-xl border-2 px-4 py-2.5 text-sm font-semibold outline-none transition-colors duration-200 ${
    state === true
      ? "border-[#16A34A] bg-[#16A34A]/5"
      : state === false
        ? "border-red-400 bg-red-50"
        : "border-[#17201A]/10 bg-[#F8FAF7] focus:border-[#16A34A]"
  }`;

const InputIcon = ({ state }) =>
  state === true ? (
    <CheckCircle2
      data-testid="input-correct-icon"
      className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#16A34A]"
    />
  ) : state === false ? (
    <XCircle
      data-testid="input-wrong-icon"
      className="pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-red-400"
    />
  ) : null;

/* TABLE_FILL / TABLE_READ — one reusable component for every subject */
const TableAnswer = ({ table, fields, inputs, setInputs, results }) => {
  const cols = table.length ? Object.keys(table[0]) : [];
  return (
    <div data-testid="table-question" className="mt-6">
      <div className="overflow-x-auto rounded-2xl border border-[#E3ECE6]">
        <table className="w-full min-w-[420px] text-sm">
          <thead>
            <tr className="bg-[#16A34A]/5">
              {cols.map((c) => (
                <th
                  key={c}
                  className="border-b border-[#E3ECE6] px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-[#17201A]/70"
                >
                  {c}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.map((row, i) => (
              <tr key={i} className="odd:bg-white even:bg-[#F8FAF7]">
                {cols.map((c) => (
                  <td key={c} className="px-4 py-2.5 font-semibold text-[#17201A]/80">
                    {row[c]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-5 space-y-3">
        {fields.map((f, i) => (
          <div key={i} className="flex items-center gap-3">
            <label className="flex-1 text-sm font-bold text-[#17201A]">{f.label}</label>
            <div className="relative w-40 shrink-0">
              <input
                data-testid={`table-answer-input-${i}`}
                value={inputs[i] || ""}
                onChange={(e) => setInputs((p) => ({ ...p, [i]: e.target.value }))}
                disabled={results !== null}
                placeholder="?"
                className={`${inputCls(results ? results[i] : null)} ${results !== null ? "pr-10" : ""}`}
              />
              {results !== null && <InputIcon state={results[i]} />}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* MATCH_COLUMNS — tap one item in each column to pair them */
const MatchAnswer = ({ columnA, columnB, pairs, setPairs, results }) => {
  const [selA, setSelA] = useState(null);
  const pickA = (i) => results === null && setSelA(selA === i ? null : i);
  const pickB = (j) => {
    if (results !== null || selA === null) return;
    setPairs((p) => ({ ...p, [selA]: j }));
    setSelA(null);
  };
  const pairedB = new Set(Object.values(pairs));
  return (
    <div data-testid="match-question" className="mt-6 grid grid-cols-2 gap-3">
      <div className="space-y-2.5">
        {columnA.map((item, i) => {
          const wrong = results !== null && results[i] === false;
          const right = results !== null && results[i] === true;
          return (
            <button
              key={i}
              data-testid={`match-a-${i}`}
              onClick={() => pickA(i)}
              className={`w-full rounded-xl border-2 px-3.5 py-3 text-left text-xs font-bold transition-colors duration-200 sm:text-sm ${
                right
                  ? "border-[#16A34A] bg-[#16A34A]/10"
                  : wrong
                    ? "border-red-400 bg-red-50"
                    : selA === i
                      ? "border-[#16A34A] bg-[#16A34A]/5"
                      : pairs[i] !== undefined
                        ? "border-[#FACC15] bg-[#FACC15]/10"
                        : "border-[#17201A]/10 bg-[#F8FAF7] hover:border-[#16A34A]/50"
              }`}
            >
              {item}
            </button>
          );
        })}
      </div>
      <div className="space-y-2.5">
        {columnB.map((item, j) => {
          const pairedA = Object.keys(pairs).find((a) => pairs[a] === j);
          const resA = results !== null && pairedA !== undefined ? results[pairedA] : null;
          return (
            <button
              key={j}
              data-testid={`match-b-${j}`}
              onClick={() => pickB(j)}
              className={`w-full rounded-xl border-2 px-3.5 py-3 text-left text-xs font-semibold transition-colors duration-200 sm:text-sm ${
                resA === true
                  ? "border-[#16A34A] bg-[#16A34A]/10 text-[#17201A]"
                  : resA === false
                    ? "border-red-400 bg-red-50 text-[#17201A]"
                    : pairedB.has(j)
                      ? "border-[#FACC15] bg-[#FACC15]/10 text-[#17201A]"
                      : "border-dashed border-[#17201A]/15 bg-white text-[#647067] hover:border-[#16A34A]/50"
              }`}
            >
              {item}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const Practice = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { subject, chapter, difficulty, klass, board, mode, reason, topic } = location.state || {};
  const profile = getProfile();

  const rawQuestions = location.state?.questions || QUESTIONS[subject]?.[chapter];
  const questions = rawQuestions
    ? rawQuestions.map((q) => ({
        qtype:
          q.question_type ||
          (q.options && q.options.length ? "MCQ" : "STATEMENT_THEORY"),
        q: q.question_text || q.q || q.question,
        hint: q.hint || null,
        steps: q.solution_steps || q.steps || (q.explanation ? [q.explanation] : []),
        answer: q.final_answer || q.answer,
        options: q.options && q.options.length ? q.options : null,
        correctOption: typeof q.correct_option === "number" ? q.correct_option : null,
        inputTable: q.input_table || null,
        answerFields: q.answer_fields || null,
        columnA: q.column_a || null,
        columnB: q.column_b || null,
        correctMapping: q.correct_mapping || null,
      }))
    : null;
  const [index, setIndex] = useState(0);
  const [showHint, setShowHint] = useState(false);
  const [showSolution, setShowSolution] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [selected, setSelected] = useState(null);
  const [inputs, setInputs] = useState({});
  const [pairs, setPairs] = useState({});
  const [results, setResults] = useState(null);
  const [outcomes, setOutcomes] = useState({});
  const [answers, setAnswers] = useState({});

  useEffect(() => {
    setElapsed(0);
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [index]);

  if (!questions) {
    return (
      <div
        data-testid="practice-empty"
        className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#F8FAF7] px-6 font-body text-[#17201A]"
      >
        <PageBlobs />
        <div className="relative w-full max-w-md rounded-[2rem] border border-[#E3ECE6] bg-white p-10 text-center shadow-sm">
          <h1 className="font-display text-2xl font-extrabold tracking-[-0.02em]">
            Questions coming soon
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-[#647067]">
            {chapter ? `${chapter} (${subject})` : "This chapter"} is in your
            syllabus — full question sets for it are being added.
          </p>
          <button
            data-testid="empty-back-btn"
            onClick={() =>
              navigate("/practice-setup", subject ? { state: { subject } } : {})
            }
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#16A34A] px-7 py-3.5 text-sm font-bold text-white transition-[background-color,transform] duration-200 hover:bg-[#15803D]"
          >
            <ArrowLeft className="h-4 w-4" />
            Choose another chapter
          </button>
        </div>
      </div>
    );
  }

  const current = questions[index];
  const isLast = index === questions.length - 1;
  const qtype = current.qtype;
  const answered = selected !== null || results !== null;

  const recordOutcome = (ok) =>
    setOutcomes((p) => (index in p ? p : { ...p, [index]: ok ? "correct" : "wrong" }));

  const handleSelect = (i) => {
    if (selected !== null) return;
    setSelected(i);
    const isCorrect =
      current.correctOption !== null
        ? i === current.correctOption
        : current.options[i] === current.answer;
    recordOutcome(isCorrect);
    setAnswers((p) => (index in p ? p : { ...p, [index]: current.options[i] }));
  };

  const describeInputs = () => {
    if (qtype === "TABLE_FILL" || qtype === "TABLE_READ") {
      return current.answerFields
        .map((f, i) => `${f.label}: ${(inputs[i] || "").trim() || "—"}`)
        .join("; ");
    }
    if (qtype === "MATCH_COLUMNS") {
      return current.columnA
        .map((a, i) => `${a} → ${pairs[i] !== undefined ? current.columnB[pairs[i]] : "—"}`)
        .join("; ");
    }
    return (inputs[0] || "").trim() || null;
  };

  const checkAnswers = () => {
    let res = {};
    if (qtype === "TABLE_FILL" || qtype === "TABLE_READ") {
      current.answerFields.forEach((f, i) => {
        res[i] = valuesMatch(inputs[i] || "", f.correct_value);
      });
    } else if (qtype === "MATCH_COLUMNS") {
      current.columnA.forEach((_, i) => {
        const want = current.correctMapping?.[String(i)] ?? current.correctMapping?.[i];
        res[i] = pairs[i] !== undefined && Number(want) === pairs[i];
      });
    } else if (qtype === "STATEMENT_NUMERICAL" || qtype === "DIAGRAM_BASED") {
      // single input vs final answer (theory uses Show Model Answer instead)
      res = { 0: valuesMatch(inputs[0] || "", current.answer) };
    } else {
      res = { 0: true };
    }
    setResults(res);
    setShowSolution(true);
    setAnswers((p) => (index in p ? p : { ...p, [index]: describeInputs() }));
    if (index in outcomes) return;
    const allRight = Object.values(res).every(Boolean);
    setOutcomes((p) => ({ ...p, [index]: allRight ? "correct" : "wrong" }));
  };

  const handleNext = () => {
    // capture a self-checked theory answer that was typed but never "checked"
    const finalAnswers =
      index in answers || !(current.qtype === "STATEMENT_THEORY") || !inputs[0]
        ? answers
        : { ...answers, [index]: inputs[0].trim() };
    const finalOutcomes =
      index in outcomes ? outcomes : { ...outcomes, [index]: "skipped" };
    if (isLast) {
      const vals = Object.values(finalOutcomes);
      const correct = vals.filter((o) => o === "correct").length;
      const review = questions.map((q, i) => ({
        question: q.q,
        question_type: q.qtype,
        options: q.options || undefined,
        answer: q.answer,
        student_answer: finalAnswers[i] ?? null,
        is_correct:
          finalOutcomes[i] === "correct"
            ? true
            : finalOutcomes[i] === "wrong"
              ? false
              : null,
      }));
      navigate("/practice-complete", {
        state: {
          subject,
          chapter,
          board,
          klass,
          difficulty,
          correct,
          wrong: vals.length - correct,
          total: questions.length,
          review,
        },
      });
    } else {
      setAnswers(finalAnswers);
      setOutcomes(finalOutcomes);
      setIndex((i) => i + 1);
      setShowHint(false);
      setShowSolution(false);
      setSelected(null);
      setInputs({});
      setPairs({});
      setResults(null);
    }
  };

  const isTable = qtype === "TABLE_FILL" || qtype === "TABLE_READ";
  const isMatch = qtype === "MATCH_COLUMNS";
  const isTextAnswer =
    qtype === "STATEMENT_NUMERICAL" ||
    ((qtype === "STATEMENT_THEORY" || qtype === "DIAGRAM_BASED") && !current.options);

  return (
    <div
      data-testid="practice-page"
      className="relative min-h-screen overflow-hidden bg-[#F8FAF7] font-body text-[#17201A]"
    >
      <PageBlobs />
      <main className="relative mx-auto max-w-2xl px-6 pb-16 pt-8">
        <div className="flex items-start justify-between gap-3">
          <p
            data-testid="practice-breadcrumb"
            className="text-xs font-bold uppercase tracking-widest text-[#17201A]/50"
          >
            Class {klass || profile.klass} · {subject} · {chapter}
            {topic && <span className="ml-1 text-[#16A34A]">· {topic}</span>}
            {difficulty && (
              <span className="ml-2 rounded-full bg-[#FACC15]/30 px-3 py-1 normal-case tracking-normal text-[#17201A]">
                {difficulty}
              </span>
            )}
          </p>
          <UserAvatar />
        </div>

        <div className="mt-5 flex items-center gap-3">
          <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-[#17201A]/8">
            <div
              className="h-full rounded-full bg-[#16A34A] transition-[width] duration-500"
              style={{ width: `${((index + 1) / questions.length) * 100}%` }}
            />
          </div>
          <span
            data-testid="question-progress"
            className="text-xs font-bold text-[#17201A]/60"
          >
            Question {index + 1} of {questions.length}
          </span>
        </div>

        {(mode === "smart" || mode === "reattempt") && (
          <p
            data-testid="practice-mode-banner"
            className="mt-4 inline-flex items-center gap-2 rounded-full border border-[#16A34A]/15 bg-[#16A34A]/8 px-4 py-1.5 text-xs font-bold text-[#14532D]"
          >
            {mode === "smart" ? "Smart Practice" : "Reattempt"}
            {reason && <span className="font-semibold normal-case text-[#647067]">· {reason}</span>}
          </p>
        )}

        <UnderstandChapter
          board={board}
          klass={klass}
          subject={subject}
          chapter={chapter}
        />

        <div
          key={index}
          data-testid="question-card"
          className="animate-fade-up mt-6 rounded-[2rem] border border-[#E3ECE6] bg-white p-7 shadow-sm sm:p-10"
        >
          <div className="flex items-start justify-between gap-3">
            <h1
              data-testid="question-text"
              className="font-display text-2xl font-bold leading-snug sm:text-3xl"
            >
              {current.q}
            </h1>
            <span
              data-testid="question-type-badge"
              className="shrink-0 rounded-full bg-[#17201A]/8 px-3 py-1 text-xs font-bold text-[#647067]"
            >
              {TYPE_LABELS[qtype] || "Theory"}
            </span>
          </div>

          <p
            data-testid="question-timer"
            className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-[#E3ECE6] bg-[#F8FAF7] px-4 py-1.5 text-xs font-bold tabular-nums text-[#647067]"
          >
            <Timer className="h-3.5 w-3.5 text-[#16A34A]" />
            {String(Math.floor(elapsed / 60)).padStart(2, "0")}:
            {String(elapsed % 60).padStart(2, "0")}
          </p>

          {isTable && current.inputTable && current.answerFields && (
            <TableAnswer
              table={current.inputTable}
              fields={current.answerFields}
              inputs={inputs}
              setInputs={setInputs}
              results={results}
            />
          )}

          {isMatch && current.columnA && current.columnB && (
            <MatchAnswer
              columnA={current.columnA}
              columnB={current.columnB}
              pairs={pairs}
              setPairs={setPairs}
              results={results}
            />
          )}

          {isTextAnswer && (
            <div className="mt-6">
              {qtype === "STATEMENT_THEORY" ? (
                <textarea
                  data-testid="theory-answer-input"
                  rows={3}
                  value={inputs[0] || ""}
                  onChange={(e) => setInputs({ 0: e.target.value })}
                  disabled={results !== null}
                  placeholder="Write your answer here..."
                  className={`${inputCls(results ? results[0] : null)} resize-none`}
                />
              ) : (
                <div className="relative">
                  <input
                    data-testid="numerical-answer-input"
                    value={inputs[0] || ""}
                    onChange={(e) => setInputs({ 0: e.target.value })}
                    disabled={results !== null}
                    placeholder="Your answer"
                    className={`${inputCls(results ? results[0] : null)} ${results !== null ? "pr-10" : ""}`}
                  />
                  {results !== null && <InputIcon state={results[0]} />}
                </div>
              )}
            </div>
          )}

          {current.options && (
            <div data-testid="practice-options" className="mt-6 grid gap-3 sm:grid-cols-2">
              {current.options.map((option, i) => {
                const isSel = selected === i;
                const isCorrect =
                  current.correctOption !== null
                    ? i === current.correctOption
                    : option === current.answer;
                let style =
                  "border-[#17201A]/10 bg-[#F8FAF7] hover:border-[#16A34A] hover:bg-[#16A34A]/5";
                if (selected !== null) {
                  if (isCorrect) style = "border-[#16A34A] bg-[#16A34A]/10";
                  else if (isSel) style = "border-red-400 bg-red-50";
                  else style = "border-[#17201A]/10 bg-[#F8FAF7] opacity-50";
                }
                return (
                  <button
                    key={i}
                    data-testid={`practice-option-${i}`}
                    onClick={() => handleSelect(i)}
                    disabled={selected !== null}
                    className={`flex items-center justify-between gap-2 rounded-2xl border-2 px-5 py-4 text-left text-sm font-semibold text-[#17201A] transition-[background-color,border-color,opacity] duration-200 ${style}`}
                  >
                    <span>
                      <span className="mr-2 font-display font-bold text-[#16A34A]">
                        {String.fromCharCode(65 + i)}.
                      </span>
                      {option}
                    </span>
                    {selected !== null && isCorrect && (
                      <CheckCircle2 className="h-5 w-5 shrink-0 text-[#16A34A]" />
                    )}
                    {selected !== null && isSel && !isCorrect && (
                      <XCircle className="h-5 w-5 shrink-0 text-red-400" />
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {showHint && (
            <div
              data-testid="hint-box"
              className="mt-6 animate-[fadeUp_0.3s_ease-out] rounded-2xl bg-[#FACC15]/15 p-5"
            >
              <p className="text-xs font-bold uppercase tracking-widest text-[#17201A]/60">
                Hint
              </p>
              <p className="mt-1.5 text-sm font-semibold text-[#17201A]/80">
                {current.hint}
              </p>
            </div>
          )}

          {showSolution && (
            <div
              data-testid="solution-box"
              className="mt-6 animate-[fadeUp_0.3s_ease-out] rounded-2xl bg-[#F8FAF7] p-5"
            >
              <p className="text-xs font-bold uppercase tracking-widest text-[#16A34A]">
                Step-by-step solution
              </p>
              <ol className="mt-3 space-y-2">
                {current.steps.map((s, i) => (
                  <li key={i} className="flex gap-2 text-sm text-[#17201A]/80">
                    <span className="font-display text-[#16A34A]">{i + 1}.</span>
                    {s}
                  </li>
                ))}
              </ol>
              <p
                data-testid="solution-answer"
                className="mt-4 rounded-xl bg-[#16A34A]/10 px-4 py-3 font-display text-lg font-bold text-[#17201A]"
              >
                {current.answer}
              </p>
            </div>
          )}

          <div className="mt-8 flex flex-wrap items-center gap-3">
            {!showHint && current.hint && (
              <button
                data-testid="show-hint-btn"
                onClick={() => setShowHint(true)}
                className="inline-flex items-center gap-2 rounded-full border-2 border-[#FACC15] bg-[#FACC15]/15 px-5 py-2.5 text-sm font-bold text-[#17201A] transition-[background-color,transform] duration-200 hover:bg-[#FACC15]/30"
              >
                <Lightbulb className="h-4 w-4" />
                Show Hint
              </button>
            )}
            {(isTable || isMatch || qtype === "STATEMENT_NUMERICAL" ||
              (qtype === "DIAGRAM_BASED" && !current.options)) &&
              results === null && (
                <button
                  data-testid="check-answers-btn"
                  onClick={checkAnswers}
                  className="inline-flex items-center gap-2 rounded-full border-2 border-[#16A34A] bg-[#16A34A]/10 px-5 py-2.5 text-sm font-bold text-[#16A34A] transition-[background-color,transform] duration-200 hover:bg-[#16A34A]/20"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Check Answer
                </button>
              )}
            {!showSolution && !answered && (
              <button
                data-testid="show-solution-btn"
                onClick={() => setShowSolution(true)}
                className="inline-flex items-center gap-2 rounded-full border border-[#E3ECE6] px-5 py-2.5 text-sm font-bold text-[#16A34A] transition-[background-color,transform] duration-200 hover:bg-[#16A34A]/5"
              >
                <BookOpen className="h-4 w-4" />
                {qtype === "STATEMENT_THEORY" ? "Show Model Answer" : "Show Solution"}
              </button>
            )}
            <button
              data-testid="next-question-btn"
              onClick={handleNext}
              className="group inline-flex items-center gap-2 rounded-full bg-[#16A34A] px-6 py-2.5 text-sm font-bold text-white shadow-sm transition-[background-color,transform] duration-200 hover:bg-[#15803D] sm:ml-auto"
            >
              {isLast ? "Finish" : "Next Question"}
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Practice;
